import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    keyDownHandler,
    RTFController,
} from '@deltek/specification-client/core/Widgets/RTFController'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Skeleton from '@material-ui/lab/Skeleton'
import {
    clipBoardHandler,
    overriddenFontStyle,
} from 'bpm/pages/ProductsPage/productsUtils'
import clsx from 'clsx'
import he from 'he'
import React, { CSSProperties, useState } from 'react'
import ContentEditable, { ContentEditableEvent } from 'react-contenteditable'

const useEditableTextBlockStyles = makeStyles((theme: Theme) => ({
    editableTextBlockTitle: {
        color: '#02A09C',
        fontWeight: 500,
        marginBottom: theme.spacing(2),
    },
    editableTextBlockContent: {
        outline: 'none',
        transition: '140ms',
    },
    editableTextBlockContentText: {
        ...theme.typography.h4,
        border: 'none',
        outline: 'none',
        color: '#5E5F61',
        width: '100%',
        '& .MuiInputBase-input.Mui-disabled': {
            color: '#5E5F61',
        },
        minHeight: 200,
        padding: theme.spacing(1),
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        '&.editableTextBlockInput[contenteditable="true"]': {
            fontWeight: 'normal',
        },
        //For overriding Browese  user-agent style/auto created style for content editable, style from ms word document etc.
        '& span,& i, & div, & b, & strong, & li, & blockquote, & p, & u': {
            ...overriddenFontStyle,
        },
    },
    textBlockEditMode: {
        outline: '1px solid #13B5EA',
    },
    characterCountContainer: {
        color: '#fff',
        height: 20,
        backgroundColor: '#13B5EA',
        alignItems: 'center',
        alignSelf: 'flex-end',
        width: 'auto',
    },
    characterCountText: {
        fontSize: pxToRem(12),
        minWidth: 61,
        textAlign: 'center',
        transition: '140ms',
    },
    editButtonContainer: {
        marginLeft: 8,
    },
    editButton: {
        '&.MuiIconButton-root': {
            border: '1px solid #15B5EA',
            padding: 8,
        },
        '& .MuiSvgIcon-root': {
            width: 16,
            height: 16,
        },
        '&:hover': {
            background: 'transparent',
        },
    },
    editPlaceholder: {
        width: 30,
        height: 0,
        background: 'red',
        marginLeft: 8,
    },
    counterPlaceholder: {
        height: 20,
    },
    error: {
        border: `1px solid ${theme.palette.error.main}`,
        outline: 'none',
    },
    maxError: {
        border: `1px solid ${theme.palette.error.main}`,
        backgroundColor: theme.palette.error.main,
    },
}))

export const countPlaintextChars = (html: string) => {
    const untaggedText = html.replace(/<[^>]*>/g, '')
    const plaintext = he.decode(untaggedText)
    return plaintext.length
}

export interface IEditableContentInfo {
    content: string
    remainingCharacters?: number
    characterCount: number
}

export interface EditableTextBlockProps {
    /**
     * Title text.
     *
     */
    title?: string

    /**
     * Show/hide the title text
     */
    disableTitleBar?: boolean

    /**
     * Text content. HTML will be escaped and shown as is.
     */
    content?: string

    /**
     * Determines whether the component is ABLE to enter edit mode or not.
     *
     * If set to false, pencil icon will be hidden.
     */
    editable?: boolean

    /**
     * Event handler for when the text content changes.
     *
     * The object contains the new text content, and the remaining character count.
     */
    onChange?: (
        contentInfo: IEditableContentInfo,
        event?: ContentEditableEvent
    ) => void

    /**
     * Maximum character count, or false if no limit.
     */
    maxChars?: number | false

    showCharCounter?: boolean

    contentEditableStyle?: CSSProperties
    isLoading?: boolean
    valid?: boolean
    onBlur?: () => void
}

export const EditableTextBlock: React.FC<EditableTextBlockProps> = ({
    title,
    disableTitleBar,
    content = '',
    editable = true,
    showCharCounter = false,
    onChange,
    onBlur,
    maxChars = 500,
    isLoading = true,
    valid,
    contentEditableStyle,
    ...props
}) => {
    const classes = useEditableTextBlockStyles()
    const [resetState, setResetState] = useState<string>('')
    const rtfHandleChange = (event: ContentEditableEvent) => {
        const textContent = event?.target?.value
        const plaintextCharCount = countPlaintextChars(textContent)

        onChange?.({
            content: textContent,
            remainingCharacters: +maxChars - plaintextCharCount,
            characterCount: plaintextCharCount,
        })
    }
    return (
        <GridContainer
            direction="column"
            className={'editableTextBlock'}
            {...props}
        >
            {!disableTitleBar && (
                <GridItem
                    container
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    className="editableTextBlockTitleBar"
                >
                    <GridItem>
                        <Typography
                            variant="h2"
                            className={clsx(
                                'editableTextBlockTitle',
                                classes.editableTextBlockTitle
                            )}
                        >
                            {title}
                        </Typography>
                    </GridItem>
                </GridItem>
            )}
            {isLoading ? (
                <LoadSkeleton />
            ) : (
                <>
                    {editable && <RTFController resetState={resetState} />}
                    <GridContainer>
                        <GridItem
                            xs
                            container
                            data-testid="editableTextBlockContentContainer"
                            direction="column"
                            className={clsx(
                                'editableTextBlockContentContainer',
                                classes.editableTextBlockContent,
                                editable && classes.textBlockEditMode,
                                !valid && classes.error
                            )}
                        >
                            <GridItem>
                                {editable ? (
                                    <ContentEditable
                                        html={(content ?? '') as string}
                                        onChange={rtfHandleChange}
                                        onPaste={(e) =>
                                            clipBoardHandler(
                                                e,
                                                maxChars || 5000
                                            )
                                        }
                                        onKeyUp={() =>
                                            setResetState(Date.now().toString())
                                        }
                                        onKeyDown={keyDownHandler}
                                        onDrop={(ev) => ev.preventDefault()}
                                        className={clsx(
                                            'editableTextBlockInput',
                                            classes.editableTextBlockContentText
                                        )}
                                        onBlur={() => setResetState('')}
                                        onClick={() =>
                                            setResetState(Date.now().toString())
                                        }
                                        disabled={!editable}
                                        data-testid="editableContent"
                                        style={contentEditableStyle}
                                    />
                                ) : (
                                    <Typography
                                        variant="h4"
                                        data-testid="editableContent"
                                        className={
                                            classes.editableTextBlockContentText
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: content ?? '', //for rendering rich html text
                                        }}
                                    />
                                )}
                            </GridItem>

                            {editable &&
                                showCharCounter &&
                                Number(maxChars) > 0 && (
                                    <GridItem
                                        container
                                        justifyContent="flex-end"
                                        alignItems="center"
                                        className={clsx({
                                            [classes.characterCountContainer]:
                                                editable && !!maxChars,
                                        })}
                                    >
                                        <Typography
                                            className={clsx(
                                                'editableTextBlockCharacterCounter',
                                                classes.characterCountText,
                                                { [classes.maxError]: !valid }
                                            )}
                                        >
                                            {countPlaintextChars(content)}/
                                            {maxChars}
                                        </Typography>
                                    </GridItem>
                                )}
                        </GridItem>
                    </GridContainer>
                </>
            )}
        </GridContainer>
    )
}

const LoadSkeleton = () => (
    <GridContainer
        direction="column"
        spacing={1}
        className="dvTextBlockLoadSkeleton"
    >
        {Array.from(Array(4)).map((_, idx) => {
            return (
                <GridItem key={idx}>
                    <Skeleton width="100%" />
                </GridItem>
            )
        })}
        <GridItem>
            <Skeleton width="40%" />
        </GridItem>
    </GridContainer>
)

export default EditableTextBlock
